<template>
  <div class="auth-shell">
    <main class="auth-panel">
      <div class="auth-panel__inner">
        <div class="auth-top">
          <a class="auth-logo-link" :href="$store.state.config.WEBSITE_URL">
            <img
              :src="$store.state.config.LOGO_URL"
              class="auth-logo"
              alt="1LINK.IO"
            />
          </a>
        </div>

        <div class="auth-body" data-testid="invite-page">
          <p v-if="view === 'loading'" class="ln-lede">
            Opening the invitation…
          </p>

          <template v-else-if="view === 'invalid'">
            <p class="ln-eyebrow">Invitation</p>
            <h1 class="ln-display">This invitation is no longer valid</h1>
            <p class="ln-lede">
              It has expired or been cancelled, or a newer one replaced it. Ask
              the person who invited you to send a new invitation.
            </p>
            <div class="auth-actions">
              <button
                type="button"
                class="ln-btn ln-btn--quiet auth-submit"
                @click="notNow"
              >
                Go to 1LINK.IO
              </button>
            </div>
          </template>

          <template v-else-if="view === 'error'">
            <p class="ln-eyebrow">Invitation</p>
            <h1 class="ln-display">Something went wrong</h1>
            <p class="ln-lede">
              The invitation could not be opened. Please reload the page.
            </p>
          </template>

          <template v-else>
            <p class="ln-eyebrow">Invitation</p>
            <h1 class="ln-display">Join {{ teamName }}</h1>
            <p class="ln-lede">
              You are invited to join <strong>{{ teamName }}</strong> on
              1LINK.IO as <strong>{{ roleLabel }}</strong
              >. The invitation is for <strong>{{ invitation.email }}</strong
              >.
            </p>

            <div v-if="view === 'signed-out'" class="auth-actions">
              <router-link
                class="ln-btn ln-btn--solid auth-submit"
                to="/signup"
                data-testid="invite-signup"
              >
                Create an account
              </router-link>
              <router-link
                class="ln-btn ln-btn--quiet auth-submit"
                to="/login"
                data-testid="invite-login"
              >
                I already have an account
              </router-link>
            </div>

            <div v-else-if="view === 'accept'" class="auth-actions">
              <button
                type="button"
                class="ln-btn ln-btn--solid auth-submit"
                :disabled="accepting"
                data-testid="invite-accept"
                @click="accept"
              >
                Accept invitation
              </button>
              <p v-if="error" class="auth-error" role="alert">{{ error }}</p>
            </div>

            <div v-else class="auth-actions">
              <p class="auth-error" role="alert">
                You are signed in as {{ signedInEmail }}. Sign in with
                {{ invitation.email }} to accept this invitation.
              </p>
              <button
                type="button"
                class="ln-btn ln-btn--solid auth-submit"
                data-testid="invite-switch-account"
                @click="switchAccount"
              >
                Log out and switch account
              </button>
            </div>
          </template>

          <button
            v-if="view !== 'loading' && view !== 'invalid'"
            type="button"
            class="auth-link"
            @click="notNow"
          >
            Not now
          </button>
        </div>
      </div>
    </main>

    <auth-showcase />
  </div>
</template>

<script>
import { auth, logout } from "../auth";
import { acceptInvitation, handleApiError, lookupInvitation } from "../api";
import {
  clearPendingInvitation,
  setPendingInvitation,
} from "../pendingInvitation";
import { ROLE_LABELS, switchWorkspace } from "../workspace";
import AuthShowcase from "../components/AuthShowcase.vue";

export default {
  name: "Invite",
  components: { AuthShowcase },
  data() {
    return {
      invitation: null,
      /** loading | ready | invalid | error */
      state: "loading",
      accepting: false,
      error: null,
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    /** not reactive: signing in or out reloads or leaves this page */
    signedInEmail() {
      return auth().currentUser?.email || null;
    },
    teamName() {
      return this.invitation?.teamName || "a team";
    },
    roleLabel() {
      return this.invitation ? ROLE_LABELS[this.invitation.role] : "";
    },
    /** what the page offers: sign in, accept, or switch account */
    view() {
      if (this.state !== "ready") return this.state;
      if (!this.signedInEmail) return "signed-out";
      return this.signedInEmail.toLowerCase() ===
        this.invitation.email.toLowerCase()
        ? "accept"
        : "other-account";
    },
  },
  watch: {
    // the router reuses this component when only the token changes (a second invitation link
    // pasted in the same tab): without this the page shows A while accept() sends B
    token() {
      return this.lookup();
    },
  },
  created() {
    return this.lookup();
  },
  methods: {
    async lookup() {
      const token = this.token;
      this.state = "loading";
      this.error = null;
      try {
        const invitation = await lookupInvitation(token);
        // the address changed while this was out: the newer lookup owns the page
        if (token !== this.token) return;
        this.invitation = invitation;
        // kept through signup and email verification: the router guard brings the person back
        setPendingInvitation({ token, ...invitation });
        this.state = "ready";
      } catch (err) {
        if (token !== this.token) return;
        const status = err?.response?.status;
        // only the API's verdict forgets the invitation: a failed lookup keeps it for a later visit
        if (status === 404 || status === 400) {
          clearPendingInvitation();
          this.state = "invalid";
        } else {
          this.state = "error";
          handleApiError(err);
        }
      } finally {
        this.$store.commit("setIsLoading", false);
      }
    },
    async accept() {
      this.accepting = true;
      this.error = null;
      try {
        const { teamId } = await acceptInvitation(this.token);
        clearPendingInvitation();
        switchWorkspace(auth().currentUser.uid, teamId);
      } catch (err) {
        this.error = handleApiError(
          err,
          "Failed to accept the invitation, please try again",
        );
        this.accepting = false;
      }
    },
    // replace, never push (AGENTS.md): the invitation is a one-time step, and back from the
    // page that follows must not land on it again
    notNow() {
      clearPendingInvitation();
      this.$router.replace(auth().currentUser ? "/" : "/login");
    },
    async switchAccount() {
      // the invitation stays stored: signing in again brings the person back here
      await logout();
      // a reload, as switchWorkspace does: a router navigation would keep the signed-out user
      // in the store, and the logout flag set, for the rest of this page's life
      window.history.replaceState(null, "", "#/login");
      window.location.reload();
    },
  },
};
</script>

<style scoped>
/* ---------- shell ------------------------------------------------------- */

.auth-shell {
  min-height: 100dvh;
  display: grid;
  grid-template-columns: 1fr;
  background: var(--ln-canvas);
}

.auth-panel {
  display: flex;
  min-height: 100dvh;
  padding: 24px 20px 40px;
}

.auth-panel__inner {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

/* ---------- top row ------------------------------------------------------ */

.auth-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 40px;
}

.auth-logo-link {
  display: inline-flex;
}

.auth-logo {
  height: 24px;
  width: auto;
}

.auth-switch {
  font-family: sans-serif;
  font-size: 13px;
  color: var(--ln-muted);
  text-decoration: none;
  white-space: nowrap;
}

.auth-switch strong {
  font-weight: 500;
  color: var(--ln-ink);
}

.auth-switch:hover strong {
  text-decoration: underline;
}

/* ---------- body ---------------------------------------------------------- */

.auth-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-bottom: 40px;
}

.ln-eyebrow {
  margin: 0 0 10px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-display {
  margin: 0;
  font-family: sans-serif;
  font-size: 32px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.ln-lede {
  margin: 10px 0 28px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-muted);
}

/* ---------- buttons -------------------------------------------------------- */

.ln-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease),
    transform 120ms var(--ln-ease);
}

.ln-btn:active {
  transform: scale(0.98);
}

.ln-btn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 2px;
}

.ln-btn__icon.v-icon {
  font-size: 15px;
  color: inherit;
}

.ln-btn--solid {
  background: var(--ln-accent);
  color: #ffffff;
}

.ln-btn--solid:hover {
  background: var(--ln-accent-hover);
}

.ln-btn--quiet {
  border-color: var(--ln-line);
  background: var(--ln-surface);
  color: var(--ln-ink-2);
}

.ln-btn--quiet:hover {
  background: var(--ln-surface-2);
  border-color: #dcdcdc;
}

.auth-google {
  width: 100%;
}

.auth-submit {
  width: 100%;
  height: 44px;
  font-size: 14px;
  margin-top: 6px;
}

/* ---------- divider --------------------------------------------------------- */

.auth-divider {
  position: relative;
  margin: 22px 0;
  text-align: center;
}

.auth-divider::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--ln-line);
}

.auth-divider span {
  position: relative;
  padding: 0 12px;
  background: var(--ln-surface);
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

/* ---------- fields ----------------------------------------------------------- */

.ln-field {
  margin-bottom: 16px;
}

.auth-body :deep(.v-field__outline) {
  color: var(--ln-line);
  --v-field-border-opacity: 1;
}

.auth-body :deep(.v-field--focused .v-field__outline) {
  color: var(--ln-accent);
  --v-field-border-opacity: 1;
}

.auth-body :deep(.v-label) {
  font-family: sans-serif;
  font-size: 13px;
  color: var(--ln-muted);
}

.auth-body :deep(.v-field__input) {
  font-family: sans-serif;
  font-size: 14px;
  color: var(--ln-ink);
}

.auth-body :deep(.v-field .v-icon) {
  font-size: 18px;
  color: var(--ln-muted);
}

.auth-row {
  display: flex;
  justify-content: flex-end;
  margin: 2px 0 22px;
}

.auth-link {
  border: 0;
  background: transparent;
  padding: 0;
  font-family: sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ln-ink-2);
  cursor: pointer;
  border-bottom: 1px solid transparent;
  transition:
    color 200ms var(--ln-ease),
    border-color 200ms var(--ln-ease);
}

.auth-link:hover {
  color: var(--ln-ink);
  border-bottom-color: var(--ln-ink);
}

/* ---------- breakpoints ------------------------------------------------------- */

@media (min-width: 960px) {
  .auth-shell {
    grid-template-columns: minmax(420px, 46%) 1fr;
  }

  .auth-panel {
    padding: 40px;
  }

  .ln-display {
    font-size: 38px;
  }
}

.auth-actions {
  display: grid;
  gap: 10px;
  margin-top: 24px;
}

.auth-error {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
}
</style>
